// Table of Specifications import from CSV. Mirrors studentImport.ts: pure parse in
// analysis/competencyMap.ts, partial success (import the good rows, report the bad
// ones), and an explicit warning when item placement had to be inferred.
import { repos } from './store.js';
import { navigate } from './router.js';
import { esc, toast } from './dom.js';
import { shareOrDownload } from '../export/csv.js';
import {
  parseTosCsv,
  toCompetencyRecords,
  formatItemSpec,
  competencyLabel,
  TOS_TEMPLATE_CSV,
  MIN_ITEMS_FOR_LEARNER,
} from '../analysis/competencyMap.js';
import type { CompetencyRecord, QuizRecord } from '../db/schema.js';

/**
 * Open the TOS import dialog for one quiz. An import REPLACES the quiz's map —
 * a TOS is a single document, so merging two of them would silently produce a
 * map that matches neither.
 */
export async function importTosDialog(quiz: QuizRecord, existing: CompetencyRecord[]): Promise<void> {
  const dlg = document.createElement('dialog');
  dlg.className = 'dlg';
  dlg.innerHTML = `<form method="dialog"><h2 class="dlg-title">Import Table of Specifications</h2>
    <p class="hint">Columns: <strong>learning_competency, items</strong> (+ optional <strong>cognitive_level</strong>).
      <strong>items</strong> is the item placement — "1-3, 7, 14". If your TOS only has a count per competency, use a
      <strong>no_of_items</strong> column instead and the items are allocated in row order.</p>
    <button type="button" class="btn btn-ghost btn-block" data-template>Download template</button>
    ${existing.length ? `<p class="hint" style="margin-top:10px"><strong>Replaces</strong> the ${existing.length} competenc${existing.length === 1 ? 'y' : 'ies'} already mapped to this quiz.</p>` : ''}
    <div class="field" style="margin-top:12px"><label for="tos_file">CSV file</label>
      <input class="input" type="file" id="tos_file" accept=".csv,text/csv"></div>
    <div id="tos_summary"></div>
    <div class="dlg-actions"><button type="button" class="btn btn-ghost" data-cancel>Cancel</button>
      <button type="button" class="btn btn-primary" data-import disabled>Import</button></div></form>`;
  document.body.appendChild(dlg);
  const close = () => {
    dlg.close();
    dlg.remove();
  };
  const fileEl = dlg.querySelector<HTMLInputElement>('#tos_file')!;
  const importBtn = dlg.querySelector<HTMLButtonElement>('[data-import]')!;
  fileEl.addEventListener('change', () => (importBtn.disabled = !fileEl.files?.length));

  dlg.querySelector('[data-template]')!.addEventListener('click', () =>
    shareOrDownload('tos_import_template.csv', TOS_TEMPLATE_CSV),
  );
  dlg.querySelector('[data-cancel]')!.addEventListener('click', close);
  dlg.addEventListener('cancel', close);

  importBtn.addEventListener('click', async () => {
    const file = fileEl.files?.[0];
    if (!file) return;
    const summary = dlg.querySelector<HTMLDivElement>('#tos_summary')!;
    const parsed = parseTosCsv(await file.text(), quiz.items);

    if (parsed.headerError) {
      summary.innerHTML = `<p class="field-err">${esc(parsed.headerError)}</p>`;
      return;
    }
    if (!parsed.rows.length) {
      summary.innerHTML = `<p class="field-err">No usable rows — nothing imported.</p>
        ${parsed.errors.length ? `<ul class="import-log">${parsed.errors.map((e) => `<li class="err">Line ${e.line}: ${esc(e.message)}</li>`).join('')}</ul>` : ''}`;
      return;
    }

    const rows = toCompetencyRecords(parsed.rows);
    try {
      await repos.setCompetencyMap(quiz.id, rows, { origin: 'local' });
    } catch (e: any) {
      summary.innerHTML = `<p class="field-err">${esc(e?.message ?? 'Import failed')}</p>`;
      return;
    }

    const mapped = new Set(rows.flatMap((c) => c.items));
    const unmapped: number[] = [];
    for (let q = 1; q <= quiz.items; q++) if (!mapped.has(q)) unmapped.push(q);
    const thin = rows.filter((c) => c.items.length < MIN_ITEMS_FOR_LEARNER);
    const thinLabels = rows
      .map((c, i) => (c.items.length < MIN_ITEMS_FOR_LEARNER ? competencyLabel(i) : ''))
      .filter(Boolean);

    const errList = parsed.errors.length
      ? `<p class="section-label">Skipped ${parsed.errors.length} row(s)</p><ul class="import-log">${parsed.errors
          .map((e) => `<li class="err">Line ${e.line}: ${esc(e.message)}</li>`)
          .join('')}</ul>`
      : '';
    const warnings: string[] = [];
    if (parsed.anyAllocated) {
      warnings.push(
        `Item numbers were allocated in row order for ${parsed.rows.filter((r) => r.allocated).length} competenc${parsed.rows.filter((r) => r.allocated).length === 1 ? 'y' : 'ies'} (no item placement in the file). Check them against the printed paper.`,
      );
    }
    if (unmapped.length) warnings.push(`Items ${formatItemSpec(unmapped)} belong to no competency.`);
    if (thin.length) {
      warnings.push(
        `${thin.length} competenc${thin.length === 1 ? 'y has' : 'ies have'} under ${MIN_ITEMS_FOR_LEARNER} items (${thinLabels.join(', ')}) — class-level only, not per learner.`,
      );
    }
    const warnList = warnings.length
      ? `<ul class="import-log">${warnings.map((w) => `<li class="warn">${esc(w)}</li>`).join('')}</ul>`
      : '';

    dlg.querySelector('form')!.innerHTML = `<h2 class="dlg-title">TOS imported</h2>
      <p><strong>${rows.length}</strong> competenc${rows.length === 1 ? 'y' : 'ies'} mapped · <strong>${mapped.size}/${quiz.items}</strong> items covered${parsed.errors.length ? ` · ${parsed.errors.length} row(s) skipped` : ''}.</p>
      ${warnList}${errList}
      <div class="dlg-actions"><button type="button" class="btn btn-primary btn-block" data-done>Done</button></div>`;
    dlg.querySelector('[data-done]')!.addEventListener('click', () => {
      close();
      toast(`${rows.length} competencies mapped`);
      navigate(location.hash || `#/quiz/${quiz.id}/competencies`);
    });
  });

  dlg.showModal();
}
